import type { WorkflowRequest, WorkflowRequestFilters, WorkflowTask } from '../model';

export type WorkflowRequestDisplayStatus = WorkflowRequest['status'] | 'AWAITING_EFFECTIVE' | 'EXECUTION_UNVERIFIED';

export function workflowRequestDisplayStatus(request: Pick<WorkflowRequest, 'status' | 'execution'>): WorkflowRequestDisplayStatus {
  const execution = request.execution;
  if (request.status === 'COMPLETED' && execution?.status === 'UNVERIFIED') return 'EXECUTION_UNVERIFIED';
  return request.status === 'COMPLETED' && execution?.status === 'RECORDED'
    && execution.effectiveDate && execution.asOfDate && execution.effectiveDate > execution.asOfDate
    ? 'AWAITING_EFFECTIVE' : request.status;
}

export function workflowRequesterLabel(name?: string) {
  const label = name?.trim();
  return label && label !== 'Requester' ? label : '이름 미등록';
}

export function workflowAdminFiltersFromSearch(search: string): WorkflowRequestFilters {
  const query = new URLSearchParams(search);
  return { all: true, ...Object.fromEntries(['search', 'templateId', 'status', 'dateFrom', 'dateTo'].flatMap((key) => {
    const value = query.get(key);
    return value ? [[key, value]] : [];
  })) };
}

export function workflowAdminRouteSearch(filters: WorkflowRequestFilters, requestId?: string) {
  const query = new URLSearchParams();
  for (const key of ['search', 'templateId', 'status', 'dateFrom', 'dateTo'] as const) if (filters[key]) query.set(key, filters[key]);
  if (requestId) query.set('request', requestId);
  return query;
}

export function isEditableWorkflowDraft(request: WorkflowRequest) {
  return ['DRAFT', 'NEEDS_CHANGES'].includes(request.status) && !request.target && request.fields !== undefined
    && request.draftApprovalSteps !== undefined && request.draftVersion !== undefined;
}

export function workflowTaskDocumentId(task: WorkflowTask) {
  return task.document?.id ?? (task.processType === 'WORKFLOW_REQUEST' ? task.subject.id : undefined);
}

export function workflowTaskDetailError(task: WorkflowTask, request: WorkflowRequest) {
  if (request.id !== workflowTaskDocumentId(task) || task.stepId !== task.step.id) return '선택한 할 일과 요청 정보가 일치하지 않습니다. 다시 불러와 주세요.';
  if (task.eventId && request.businessProcessEventId && task.eventId !== request.businessProcessEventId) return '요청의 결재 회차가 변경되었습니다. 최신 할 일을 다시 불러와 주세요.';
  const step = request.approvalSteps?.find((item) => item.order === task.step.order);
  if (request.status !== 'IN_PROGRESS' || task.step.status !== 'IN_PROGRESS' || step?.status !== 'IN_PROGRESS') return '이 결재 단계는 현재 처리할 수 없습니다. 목록을 새로고침해 최신 상태를 확인해 주세요.';
  return undefined;
}

const taskProcessLabels: Record<string, string> = {
  WORKFLOW_REQUEST: '일반 요청', TIME_OFF: '휴가', TIME_OFF_REQUEST: '휴가', TIME_OFF_CANCELLATION: '휴가 취소',
  EMPLOYMENT_CONTRACT_CHANGE: '근로계약 변경', JOB_CHANGE: '인사 발령', STAFFING_PLAN: '인사 변경안', HIRE: '입사', END_WORKER: '퇴직·계약 종료',
  LEAVE_OF_ABSENCE: '휴직', RETURN_FROM_LEAVE: '복직', CONTACT_CHANGE: '연락처 변경',
  PERSONAL_INFORMATION_CHANGE: '개인정보 변경', ORGANIZATION_CHANGE: '조직 변경',
  TIME_OFF_CORRECTION: '휴가 정정', POSITION_HEADCOUNT_CHANGE: '포지션·정원 변경',
};
export const workflowTaskProcessLabel = (processType: string) => taskProcessLabels[processType] ?? '기타 요청';
export function workflowTaskWaitingLabel(requestedAt: string, now = Date.now()) {
  const requested = Date.parse(requestedAt);
  if (!Number.isFinite(requested)) return '요청일 미등록';
  const days = Math.max(0, Math.floor((now - requested) / 86_400_000));
  return days ? `${days.toLocaleString()}일 대기` : '1일 미만 대기';
}
